import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import {
  GoogleMap,
  Marker,
  StandaloneSearchBox,
  useLoadScript,
} from '@react-google-maps/api';
import _ from 'lodash';

import styles from './googleMaps.module.scss';
import { actions } from '../../state';
import mapPin from './../../assets/icons/mapPin.svg';

const DEFAULT_CENTER = {
  lat: 54.6852678697085,
  lng: 25.280707169708535,
};

const CONTAINER_STYLE = {
  width: '100%',
  maxWidth: '520px',
};

const LOADER_STYLE = {
  ...CONTAINER_STYLE,
  height: '370px',
};

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyDYgKJ2r0pzNnjbN5LuJ98JxYYLcsOCbb4';

const MapWithASearchBox = ({ dispatch, address, invalidInput }) => {
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [markers, setMarkers] = useState([]);
  const [inputValue, setInputValue] = useState(
    address ? `${address.address} ${address.city || ''}` : ''
  );
  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const libraries = useMemo(() => ['geometry', 'drawing', 'places'], []);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (address && address.coordinates) {
      const position = {
        lat: address.coordinates[0],
        lng: address.coordinates[1],
      };
      setMarkers([{ position }]);
      setCenter(position);
      if (mapRef.current) {
        mapRef.current.setCenter(position);
      }
      setInputValue(`${address.address} ${address.city || ''}`);
    } else {
      setMarkers([]);
      setCenter(DEFAULT_CENTER);
      setInputValue('');
    }
  }, [address]);

  const handleMapLoad = useCallback(map => {
    mapRef.current = map;
  }, []);

  const handleSearchBoxLoad = useCallback(ref => {
    searchBoxRef.current = ref;
  }, []);

  const handlePlacesChanged = useCallback(() => {
    const places = searchBoxRef.current ? searchBoxRef.current.getPlaces() : [];
    if (!places || !places.length) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    places.forEach(place => {
      if (place.geometry && place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else if (place.geometry && place.geometry.location) {
        bounds.extend(place.geometry.location);
      }
    });
    if (mapRef.current) {
      mapRef.current.fitBounds(bounds);
    }

    const nextMarkers = places
      .filter(place => place.geometry && place.geometry.location)
      .map(place => ({
        position: place.geometry.location,
      }));
    const nextCenter = _.get(nextMarkers, '0.position', center);

    setCenter(nextCenter);
    setMarkers(nextMarkers);

    const pl = places[0];
    const addressLine = pl.formatted_address
      ? pl.formatted_address.split(',')[0]
      : '';
    const coordinates = nextCenter.toJSON ? nextCenter.toJSON() : nextCenter;
    let city = '';
    if (pl.address_components) {
      const cityComponent = pl.address_components.find(comp =>
        comp.types.includes('locality')
      );
      city = cityComponent ? cityComponent.long_name : '';
    } else {
      city = pl.vicinity || '';
    }
    const data = {
      city,
      address: addressLine,
      coordinates: [coordinates.lat, coordinates.lng],
    };

    setInputValue(`${data.address} ${data.city || ''}`);
    dispatch(actions.addEditServices.pushPlaceAction(data));
  }, [center, dispatch]);

  const handleMarkerDragEnd = useCallback(
    e => {
      if (!e || !e.latLng) {
        return;
      }
      const changedCoord = e.latLng.toJSON();
      dispatch(actions.addEditServices.pushNewCoordAction(changedCoord));
    },
    [dispatch]
  );

  const handleSearchInputChange = useCallback(e => {
    setInputValue(e.target.value);
  }, []);

  if (!isLoaded) {
    return <div style={LOADER_STYLE} />;
  }

  return (
    <div className={styles.mapContainer} style={CONTAINER_STYLE}>
      <div className={styles.searchBoxWrapper}>
        <StandaloneSearchBox
          onLoad={handleSearchBoxLoad}
          onPlacesChanged={handlePlacesChanged}
        >
          <input
            type="text"
            placeholder="Įveskite plovyklos adresą"
            value={inputValue}
            onChange={handleSearchInputChange}
            style={{
              zIndex: `100`,
              borderWidth: 1,
              borderColor: invalidInput ? 'red' : 'transparent',
            }}
            className={styles.mapSearchInput}
          />
        </StandaloneSearchBox>
      </div>

      <div className={styles.underSearchBox}>
        <p>Tempiant žymeklį patikslinkite lokaciją</p>
      </div>

      <GoogleMap
        onLoad={handleMapLoad}
        zoom={15}
        center={center}
        mapContainerClassName={styles.mapElement}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {markers.map((marker, index) => (
          <Marker
            key={index}
            position={marker.position}
            draggable={true}
            onDragEnd={handleMarkerDragEnd}
            icon={mapPin}
          />
        ))}
      </GoogleMap>
    </div>
  );
};

export default connect()(MapWithASearchBox);
